// Sprint 2 — Reorganización de configuración
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { css } from "styled-system/css";
import type { NotifConfig } from "@prisma/client";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { NotifSettingsForm } from "@/components/dashboard/notif-settings-form";
import { NotifReminderToggles } from "@/components/dashboard/notif-reminder-toggles";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { ProFeature } from "@/components/ui/ProFeature";
import { useToast } from "@/hooks/useToast";

type NotifCfg = NotifConfig & { planFeatures?: { whatsapp: boolean; payments: boolean } };

const defaults: NotifCfg = {
  id: "",
  professionalId: "",
  sendConfirmEmail: true,
  sendConfirmSMS: false,
  sendReminder24h: true,
  sendReminder1h: false,
  sendCancelEmail: true,
  whatsappEnabled: false,
  notifyNewBookingEmail: true,
};

const CLIENT_NOTIFICATIONS: {
  icon: string;
  label: string;
  channels: string;
  preview: string;
  when: string;
  color: string;
}[] = [
  {
    icon: "✅",
    label: "Confirmación de turno",
    channels: "Email · WhatsApp (plan Pro)",
    when: "Inmediatamente al confirmar la reserva",
    color: "#f0fdf4",
    preview:
      "Hola Juan, tu turno con Mi negocio quedó registrado.\nServicio: Corte de cabello\nFecha y hora: 15/04/2026 14:00\nPodés cancelar hasta 24 horas antes de tu turno.",
  },
  {
    icon: "💳",
    label: "Seña pendiente",
    channels: "Email · WhatsApp (plan Pro)",
    when: "Al reservar cuando el pago online está pendiente",
    color: "#fffbeb",
    preview:
      "Hola Juan, para confirmar tu turno con Mi negocio necesitás pagar la seña de $1.500.\nServicio: Corte de cabello\nFecha y hora: 15/04/2026 14:00\nLink de pago con Mercado Pago.",
  },
  {
    icon: "❌",
    label: "Cancelación",
    channels: "Email · WhatsApp (plan Pro)",
    when: "Cuando el profesional o el propio cliente cancela el turno",
    color: "#fff1f2",
    preview: "Hola Juan, tu turno con Mi negocio fue cancelado.",
  },
  {
    icon: "🔄",
    label: "Turno reprogramado",
    channels: "WhatsApp (plan Pro)",
    when: "Cuando el profesional modifica fecha u horario del turno",
    color: "#f0f9ff",
    preview:
      "Hola Juan, tu turno con Mi negocio fue actualizado.\nNuevo horario: 16/04/2026 10:00\nPodés gestionar tu reserva desde el enlace que recibiste.",
  },
  {
    icon: "🔔",
    label: "Recordatorio 24 horas antes",
    channels: "Email · WhatsApp (plan Pro)",
    when: "Automático: ~24 h antes del turno confirmado",
    color: "#f5f3ff",
    preview:
      "Hola Juan, te recordamos tu turno de mañana con Mi negocio.\nServicio: Corte de cabello\nFecha y hora: 15/04/2026 14:00",
  },
  {
    icon: "⏰",
    label: "Recordatorio 1 hora antes",
    channels: "Email · WhatsApp (plan Pro)",
    when: "Automático: ~1 h antes del turno confirmado",
    color: "#fdf4ff",
    preview:
      "Hola Juan, en una hora tenés turno con Mi negocio.\nServicio: Corte de cabello\nFecha y hora: 15/04/2026 14:00",
  },
];

type ReminderRule = { id: string; minutesBefore: number };

const REMINDER_OPTIONS: { minutes: number; label: string }[] = [
  { minutes: 30,   label: "30 minutos antes" },
  { minutes: 60,   label: "1 hora antes" },
  { minutes: 90,   label: "1 hora y media antes" },
  { minutes: 120,  label: "2 horas antes" },
  { minutes: 180,  label: "3 horas antes" },
  { minutes: 240,  label: "4 horas antes" },
  { minutes: 360,  label: "6 horas antes" },
  { minutes: 720,  label: "12 horas antes" },
  { minutes: 2880, label: "48 horas antes" },
];

function minutesLabel(m: number): string {
  const opt = REMINDER_OPTIONS.find((o) => o.minutes === m);
  if (opt) return opt.label.replace(" antes", "");
  if (m >= 1440) return `${Math.round(m / 1440)} día${Math.round(m / 1440) !== 1 ? "s" : ""}`;
  if (m >= 60) return `${Math.round(m / 60)} hora${Math.round(m / 60) !== 1 ? "s" : ""}`;
  return `${m} min`;
}

export function DashboardNotifications() {
  const { refresh } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState<"me" | "client">("me");
  const [cfg, setCfg] = useState<NotifCfg | null>(null);
  const [plan, setPlan] = useState("FREE");
  const [reminderPrefs, setReminderPrefs] = useState<{ notify24h: boolean; notify1h: boolean } | null>(null);
  const [reminderRules, setReminderRules] = useState<ReminderRule[]>([]);
  const [addingRule, setAddingRule] = useState(false);
  const [selectedMinutes, setSelectedMinutes] = useState<number>(120);
  const [savingRule, setSavingRule] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    let c = false;
    void (async () => {
      const [rN, rM] = await Promise.all([
        fetchWithSessionSync("/api/dashboard/notif-config", { credentials: "include" }, refresh),
        fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh),
      ]);
      if (c) return;
      if (rM.ok) {
        const d = (await rM.json()) as {
          professional?: { plan?: string; notify24h?: boolean; notify1h?: boolean };
        };
        setPlan(d.professional?.plan ?? "FREE");
        setReminderPrefs({
          notify24h: d.professional?.notify24h !== false,
          notify1h: Boolean(d.professional?.notify1h),
        });
      } else {
        setReminderPrefs({ notify24h: true, notify1h: false });
      }
      if (!rN.ok) {
        setCfg({ ...defaults });
        return;
      }
      const row = (await rN.json()) as NotifCfg | null;
      setCfg(row ?? { ...defaults });
    })();
    return () => {
      c = true;
    };
  }, [refresh]);

  useEffect(() => {
    if (plan !== "PRO" && plan !== "ENTERPRISE") return;
    void fetchWithSessionSync("/api/dashboard/reminder-rules", { credentials: "include" }, refresh)
      .then((r) => r.ok ? r.json() : [])
      .then((rules: ReminderRule[]) => setReminderRules(rules));
  }, [plan, refresh]);

  async function addReminderRule() {
    setSavingRule(true);
    try {
      const r = await fetchWithSessionSync("/api/dashboard/reminder-rules", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ minutesBefore: selectedMinutes }),
      }, refresh);
      if (r.ok) {
        const rule = (await r.json()) as ReminderRule;
        setReminderRules((prev) => [...prev, rule].sort((a, b) => a.minutesBefore - b.minutesBefore));
        setAddingRule(false);
        toast.success("Recordatorio agregado");
      } else {
        const { error } = (await r.json()) as { error?: string };
        toast.error(error ?? "No se pudo agregar.");
      }
    } finally {
      setSavingRule(false);
    }
  }

  async function deleteReminderRule(ruleId: string) {
    const r = await fetchWithSessionSync(`/api/dashboard/reminder-rules/${ruleId}`, {
      method: "DELETE",
      credentials: "include",
    }, refresh);
    if (r.ok) {
      setReminderRules((prev) => prev.filter((x) => x.id !== ruleId));
      toast.success("Recordatorio eliminado");
    } else {
      toast.error("No se pudo eliminar.");
    }
  }

  async function patchWhatsApp(whatsappEnabled: boolean) {
    if (!cfg) return;
    const r = await fetchWithSessionSync(
      "/api/dashboard/notif-config",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ whatsappEnabled }),
      },
      refresh,
    );
    if (r.ok) {
      setCfg({ ...cfg, whatsappEnabled });
      toast.success("Guardado");
    } else toast.error("No se pudo guardar.");
  }

  if (!cfg) {
    return (
      <DashboardPageShell eyebrow="Comunicación" title="Avisos">
        <LoadingFrame variant="light" minHeight={280} />
      </DashboardPageShell>
    );
  }

  const isPro = plan === "PRO" || plan === "ENTERPRISE";

  return (
    <DashboardPageShell
      eyebrow="Comunicación"
      title="Avisos"
      description="Elegí qué mensajes automáticos enviar a vos y a tus clientes."
    >
      <div className={css({ display: "flex", gap: "2", mb: "6", flexWrap: "wrap" })}>
        <button
          type="button"
          onClick={() => setTab("me")}
          className={css({
            px: "4",
            py: "2",
            rounded: "lg",
            fontSize: "sm",
            fontWeight: "semibold",
            border: "none",
            cursor: "pointer",
            bg: tab === "me" ? "rgba(99, 102, 241, 0.15)" : "#f1f5f9",
            color: tab === "me" ? "token(colors.brand.700)" : "#64748b",
          })}
        >
          Para mí
        </button>
        <button
          type="button"
          onClick={() => setTab("client")}
          className={css({
            px: "4",
            py: "2",
            rounded: "lg",
            fontSize: "sm",
            fontWeight: "semibold",
            border: "none",
            cursor: "pointer",
            bg: tab === "client" ? "rgba(99, 102, 241, 0.15)" : "#f1f5f9",
            color: tab === "client" ? "token(colors.brand.700)" : "#64748b",
          })}
        >
          Para el cliente
        </button>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.18 }}
        >
          {tab === "me" ? (
            <div className={css({ display: "flex", flexDir: "column", gap: "6" })}>
              <NotifSettingsForm
                initial={{
                  sendConfirmEmail: cfg.sendConfirmEmail,
                  sendCancelEmail: cfg.sendCancelEmail,
                  notifyNewBookingEmail: cfg.notifyNewBookingEmail !== false,
                }}
              />
            </div>
          ) : (
            <div className={css({ display: "flex", flexDir: "column", gap: "5" })}>
              <ProFeature planRequired="advance" currentPlan={plan}>
                <div className={css({ p: "5", rounded: "xl", borderWidth: "1px", borderColor: "#e2e8f0", bg: "white" })}>
                  <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "4" })}>
                    <div>
                      <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
                        Enviar notificaciones por WhatsApp
                      </p>
                      <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>
                        Canal adicional (confirmaciones, recordatorios, cancelaciones).
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={cfg.whatsappEnabled}
                      disabled={!isPro}
                      onClick={() => isPro && void patchWhatsApp(!cfg.whatsappEnabled)}
                      className={css({
                        w: "11",
                        h: "6",
                        rounded: "full",
                        position: "relative",
                        flexShrink: 0,
                        bg: cfg.whatsappEnabled ? "rgba(129, 140, 248, 0.9)" : "#e2e8f0",
                        borderWidth: "1px",
                        borderColor: cfg.whatsappEnabled ? "rgba(99,102,241,0.4)" : "#cbd5e1",
                        cursor: isPro ? "pointer" : "not-allowed",
                        opacity: isPro ? 1 : 0.6,
                      })}
                    >
                      <span
                        className={css({
                          position: "absolute",
                          top: "3px",
                          left: cfg.whatsappEnabled ? "calc(100% - 22px)" : "3px",
                          w: "18px",
                          h: "18px",
                          rounded: "full",
                          bg: "white",
                          transition: "left 0.2s",
                        })}
                      />
                    </button>
                  </div>
                </div>
              </ProFeature>

              {reminderPrefs ? (
                <div>
                  <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: "3" })}>
                    Recordatorios automáticos al cliente
                  </p>
                  <NotifReminderToggles initial={reminderPrefs} onPrefsChange={setReminderPrefs} />
                </div>
              ) : null}

              {/* Recordatorios personalizados — solo Pro+ */}
              {isPro && (
                <div>
                  <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: "3" })}>
                    Recordatorios personalizados
                  </p>
                  <div className={css({ p: "4", rounded: "xl", borderWidth: "1px", borderColor: "#e2e8f0", bg: "white", display: "flex", flexDir: "column", gap: "3" })}>
                    {/* Reglas existentes */}
                    {reminderRules.length > 0 && (
                      <div className={css({ display: "flex", flexWrap: "wrap", gap: "2" })}>
                        {reminderRules.map((rule) => (
                          <div
                            key={rule.id}
                            className={css({
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "1.5",
                              px: "3",
                              py: "1.5",
                              rounded: "full",
                              bg: "rgba(99,102,241,0.08)",
                              borderWidth: "1px",
                              borderColor: "rgba(99,102,241,0.2)",
                              fontSize: "xs",
                              fontWeight: "semibold",
                              color: "token(colors.brand.700)",
                            })}
                          >
                            <span>🔔</span>
                            <span>{minutesLabel(rule.minutesBefore)}</span>
                            <button
                              type="button"
                              onClick={() => void deleteReminderRule(rule.id)}
                              className={css({
                                ml: "0.5",
                                w: "4",
                                h: "4",
                                rounded: "full",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bg: "transparent",
                                border: "none",
                                color: "token(colors.brand.500)",
                                cursor: "pointer",
                                fontSize: "12px",
                                lineHeight: "1",
                                _hover: { bg: "rgba(99,102,241,0.15)" },
                              })}
                              title="Eliminar recordatorio"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Límite alcanzado */}
                    {reminderRules.length >= 2 && (
                      <p className={css({ fontSize: "xs", color: "#64748b" })}>
                        Límite de 2 recordatorios personalizados alcanzado.
                      </p>
                    )}

                    {/* Agregar nuevo */}
                    {reminderRules.length < 2 && !addingRule && (
                      <button
                        type="button"
                        onClick={() => setAddingRule(true)}
                        className={css({
                          alignSelf: "flex-start",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "1.5",
                          px: "3",
                          py: "1.5",
                          rounded: "lg",
                          fontSize: "xs",
                          fontWeight: "semibold",
                          color: "token(colors.brand.600)",
                          bg: "rgba(99,102,241,0.07)",
                          border: "none",
                          cursor: "pointer",
                          _hover: { bg: "rgba(99,102,241,0.13)" },
                        })}
                      >
                        + Agregar recordatorio
                      </button>
                    )}

                    {/* Selector inline — dropdown custom */}
                    {addingRule && (
                      <div className={css({ display: "flex", alignItems: "center", gap: "2", flexWrap: "wrap" })}>
                        {/* Dropdown custom */}
                        <div className={css({ position: "relative" })}>
                          <button
                            type="button"
                            onClick={() => setDropdownOpen((v) => !v)}
                            className={css({
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "2",
                              px: "3",
                              py: "1.5",
                              rounded: "lg",
                              borderWidth: "1px",
                              borderColor: dropdownOpen ? "rgba(99,102,241,0.5)" : "#e2e8f0",
                              fontSize: "sm",
                              color: "#0f172a",
                              bg: "white",
                              cursor: "pointer",
                              minW: "180px",
                              justifyContent: "space-between",
                              boxShadow: dropdownOpen ? "0 0 0 2px rgba(99,102,241,0.15)" : "none",
                            })}
                          >
                            <span>{REMINDER_OPTIONS.find((o) => o.minutes === selectedMinutes)?.label ?? "Elegir intervalo"}</span>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transition: "transform 0.15s", transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}>
                              <path d="M2 4l4 4 4-4" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          {dropdownOpen && (
                            <>
                              {/* Overlay para cerrar al hacer click afuera */}
                              <div
                                style={{ position: "fixed", inset: 0, zIndex: 49 }}
                                onClick={() => setDropdownOpen(false)}
                              />
                              <div className={css({
                                position: "absolute",
                                top: "calc(100% + 4px)",
                                left: 0,
                                zIndex: 50,
                                bg: "white",
                                borderWidth: "1px",
                                borderColor: "#e2e8f0",
                                rounded: "xl",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                                py: "1",
                                minW: "200px",
                                overflow: "hidden",
                              })}>
                                {REMINDER_OPTIONS
                                  .filter((o) => !reminderRules.some((r) => r.minutesBefore === o.minutes))
                                  .map((o) => (
                                    <button
                                      key={o.minutes}
                                      type="button"
                                      onClick={() => { setSelectedMinutes(o.minutes); setDropdownOpen(false); }}
                                      className={css({
                                        display: "block",
                                        w: "100%",
                                        textAlign: "left",
                                        px: "3",
                                        py: "2",
                                        fontSize: "sm",
                                        color: o.minutes === selectedMinutes ? "token(colors.brand.700)" : "#0f172a",
                                        bg: o.minutes === selectedMinutes ? "rgba(99,102,241,0.07)" : "transparent",
                                        fontWeight: o.minutes === selectedMinutes ? "semibold" : "normal",
                                        border: "none",
                                        cursor: "pointer",
                                        _hover: { bg: "rgba(99,102,241,0.06)" },
                                      })}
                                    >
                                      {o.label}
                                    </button>
                                  ))}
                              </div>
                            </>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => { setDropdownOpen(false); void addReminderRule(); }}
                          disabled={savingRule}
                          className={css({
                            px: "3",
                            py: "1.5",
                            rounded: "lg",
                            fontSize: "sm",
                            fontWeight: "semibold",
                            bg: "rgba(99,102,241,0.9)",
                            color: "white",
                            border: "none",
                            cursor: savingRule ? "not-allowed" : "pointer",
                            opacity: savingRule ? 0.6 : 1,
                          })}
                        >
                          {savingRule ? "Guardando…" : "Guardar"}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setAddingRule(false); setDropdownOpen(false); }}
                          className={css({
                            px: "2",
                            py: "1.5",
                            rounded: "lg",
                            fontSize: "sm",
                            color: "#64748b",
                            bg: "transparent",
                            border: "none",
                            cursor: "pointer",
                          })}
                        >
                          Cancelar
                        </button>
                      </div>
                    )}

                    {reminderRules.length === 0 && !addingRule && (
                      <p className={css({ fontSize: "xs", color: "#94a3b8" })}>
                        Sin recordatorios personalizados. Agregá hasta 2 avisos automáticos.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: "3" })}>
                  Mensajes que recibe el cliente
                </p>
                <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
                  {CLIENT_NOTIFICATIONS.map((n) => (
                    <div
                      key={n.label}
                      className={css({ p: "4", rounded: "xl", borderWidth: "1px", borderColor: "#e2e8f0", bg: "white" })}
                    >
                      <div className={css({ display: "flex", alignItems: "flex-start", gap: "3", mb: "2" })}>
                        <span className={css({ fontSize: "20px", lineHeight: "1", flexShrink: 0, mt: "0.5" })}>{n.icon}</span>
                        <div className={css({ flex: 1, minW: 0 })}>
                          <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>{n.label}</p>
                          <p className={css({ fontSize: "xs", color: "#64748b", mt: "0.5" })}>{n.when}</p>
                          <div className={css({ display: "flex", gap: "1.5", mt: "1", flexWrap: "wrap" })}>
                            {n.channels.split(" · ").map((ch) => (
                              <span
                                key={ch}
                                className={css({
                                  fontSize: "10px",
                                  fontWeight: "semibold",
                                  px: "1.5",
                                  py: "0.5",
                                  rounded: "md",
                                  bg: ch.includes("WhatsApp") ? "#dcfce7" : "#eff6ff",
                                  color: ch.includes("WhatsApp") ? "#166534" : "#1e40af",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                })}
                              >
                                {ch}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <pre
                        className={css({
                          p: "3",
                          rounded: "lg",
                          bg: n.color,
                          fontSize: "xs",
                          color: "#334155",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          fontFamily: "inherit",
                          lineHeight: "1.6",
                          borderWidth: "1px",
                          borderColor: "#e2e8f0",
                        })}
                      >
                        {n.preview}
                      </pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </DashboardPageShell>
  );
}
